/**
 * Navigation structure — the single source of truth for what appears in the
 * header and in each section's tab strip.
 *
 * The two admin sections split on supply versus delivery: REGISTRY is what
 * this installation offers, PROVISIONING is what each project actually gets.
 * Provisioning comes first because composing and assigning packs is the daily
 * task; curating the registry is not.
 *
 * Work that adds a navigation surface REGISTERS it here; it does not edit
 * Header.tsx. Two registries collect sections that grow by registration:
 *
 *   RUNS       — agentic pipeline jobs (#101)
 *   DASHBOARDS — the AI usage gateway and the observability dashboards
 *
 * A section marked `reserved` stays hidden until its registry is non-empty,
 * so neither ships as an empty destination.
 *
 * See docs/plans/2026-09-14-admin-nav-consolidation-design.md
 */

import type { TabNavItem } from '@components/ui/TabNav';
import { ROUTES } from './constants';

export type NavTab = TabNavItem;

export interface NavSection {
  label: string;
  to: string;
  adminOnly: boolean;
  /** Sub-tabs. The first entry MUST point at the section's own path. */
  tabs: NavTab[];
  /** Reserved sections stay hidden while `tabs` is empty. */
  reserved?: boolean;
}

/**
 * Dashboards register here. AI usage (#105) is the first; the observability
 * work adds the next.
 *
 * Contract for registrants: the first entry pushed must use the section's
 * own path — `to: ROUTES.DASHBOARDS` — as its `to`, so the header's
 * "Dashboards" link lands on the section's index route. Subsequent entries
 * use their own child paths.
 *
 * Adding the second dashboard therefore means: append an entry with its own
 * `ROUTES.DASHBOARDS_*` path here, add the matching child `<Route>` under the
 * Dashboards section in App.tsx, and leave AI usage where it is as the index.
 * At two entries TabNav stops hiding itself and the strip appears on its own.
 * AiUsageTab.test.tsx holds this contract.
 */
export const DASHBOARDS: NavTab[] = [
  { label: 'AI usage', to: ROUTES.DASHBOARDS },
];

/**
 * Agent-run views register here. The run list (#101) is the first; a run's
 * detail page is a child route of the section, not a tab.
 *
 * Contract for registrants: the first entry pushed must use the section's
 * own path — `to: ROUTES.RUNS` — as its `to`, so the header's "Runs" link
 * lands on the section's index route. Subsequent entries use their own
 * child paths.
 */
export const RUNS: NavTab[] = [
  { label: 'All runs', to: ROUTES.RUNS },
];

export const NAV_SECTIONS: NavSection[] = [
  {
    label: 'Workspaces',
    to: ROUTES.HOME,
    adminOnly: false,
    tabs: [],
  },
  {
    label: 'Runs',
    to: ROUTES.RUNS,
    adminOnly: false,
    tabs: RUNS,
    reserved: true,
  },
  {
    label: 'Dashboards',
    to: ROUTES.DASHBOARDS,
    adminOnly: false,
    tabs: DASHBOARDS,
    reserved: true,
  },
  {
    label: 'Settings',
    to: ROUTES.SETTINGS,
    adminOnly: false,
    tabs: [
      { label: 'Git credentials', to: ROUTES.SETTINGS },
      { label: 'Feature preferences', to: ROUTES.SETTINGS_FEATURES },
      { label: 'Profile', to: ROUTES.SETTINGS_PROFILE },
    ],
  },
  {
    label: 'Provisioning',
    to: ROUTES.ADMIN_PROVISIONING,
    adminOnly: true,
    tabs: [
      { label: 'Packs', to: ROUTES.ADMIN_PROVISIONING },
      { label: 'Projects', to: ROUTES.ADMIN_PROVISIONING_PROJECTS },
    ],
  },
  {
    label: 'Registry',
    to: ROUTES.ADMIN_REGISTRY,
    adminOnly: true,
    tabs: [
      { label: 'Features', to: ROUTES.ADMIN_REGISTRY },
      { label: 'Images', to: ROUTES.ADMIN_REGISTRY_IMAGES },
      { label: 'AI models', to: ROUTES.ADMIN_REGISTRY_AI_MODELS },
    ],
  },
  {
    label: 'System',
    to: ROUTES.ADMIN_SYSTEM,
    adminOnly: true,
    tabs: [
      { label: 'Git servers', to: ROUTES.ADMIN_SYSTEM },
      { label: 'Login providers', to: ROUTES.ADMIN_SYSTEM_LOGINS },
    ],
  },
];

/** Sections the given user should see in the header, in display order. */
export function visibleSections(isAdmin: boolean): NavSection[] {
  return NAV_SECTIONS.filter((section) => {
    if (section.adminOnly && !isAdmin) return false;
    if (section.reserved === true && section.tabs.length === 0) return false;
    return true;
  });
}

/** Tabs for the section owning `to`, or [] if there is no such section. */
export function sectionTabs(to: string): NavTab[] {
  return NAV_SECTIONS.find((section) => section.to === to)?.tabs ?? [];
}
